import React from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ImageIcon } from "lucide-react";
import { validateImageUrl, type ImageUrlVerdict } from "@/utils/imageUrlUtils";

interface ImageUrlButtonProps {
  editor: Editor;
}

const WIDTH_PRESETS = [
  { label: "Small", value: 150 },
  { label: "Medium", value: 300 },
  { label: "Full", value: 600 },
];

const MIN_WIDTH = 1;
const MAX_WIDTH = 2000;
const CONVENTIONAL_WIDTH = 600;
const VALIDATION_DEBOUNCE_MS = 300;

const clampWidth = (value: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));

const deriveAltFromUrl = (url: string): string => {
  if (!url || url.startsWith("data:")) {
    return "";
  }
  const withoutQuery = url.split(/[?#]/)[0];
  const segments = withoutQuery.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  const last = segments[segments.length - 1];
  let decoded = last;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    decoded = last;
  }
  const withoutExtension = decoded.replace(/\.[a-zA-Z0-9]+$/, "");
  return withoutExtension.replace(/[-_]+/g, " ").trim();
};

const ImageUrlButton: React.FC<ImageUrlButtonProps> = ({ editor }) => {
  const [open, setOpen] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [url, setUrl] = React.useState("");
  const [alt, setAlt] = React.useState("");
  const [altTouched, setAltTouched] = React.useState(false);
  const [width, setWidth] = React.useState("");
  const [originalSize, setOriginalSize] = React.useState(false);
  const [verdict, setVerdict] = React.useState<ImageUrlVerdict | null>(null);
  const [previewError, setPreviewError] = React.useState(false);

  // Debounced validation, reset preview error whenever the URL changes.
  React.useEffect(() => {
    setPreviewError(false);
    if (!url) {
      setVerdict(null);
      return;
    }
    const timer = setTimeout(() => {
      setVerdict(validateImageUrl(url));
    }, VALIDATION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [url]);

  // Auto-fill alt text from the URL until the user types in the alt field.
  React.useEffect(() => {
    if (!altTouched) {
      setAlt(deriveAltFromUrl(url));
    }
  }, [url, altTouched]);

  const resetState = () => {
    setIsEditing(false);
    setUrl("");
    setAlt("");
    setAltTouched(false);
    setWidth("");
    setOriginalSize(false);
    setVerdict(null);
    setPreviewError(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetState();
      return;
    }

    const active = editor.isActive("image");
    setIsEditing(active);

    if (!active) {
      setUrl("");
      setAlt("");
      setAltTouched(false);
      setWidth("");
      setOriginalSize(false);
      setVerdict(null);
      setPreviewError(false);
      return;
    }

    const attrs = editor.getAttributes("image");
    const attrSrc = typeof attrs.src === "string" ? attrs.src : "";
    const attrAlt = typeof attrs.alt === "string" ? attrs.alt : "";
    const parsedWidth = Number(attrs.width);

    setUrl(attrSrc);
    setAlt(attrAlt);
    // Existing alt text is intentional; don't let URL-derived auto-fill clobber it.
    setAltTouched(true);
    setPreviewError(false);

    if (!Number.isNaN(parsedWidth) && parsedWidth > 0) {
      setWidth(String(parsedWidth));
      setOriginalSize(false);
    } else {
      setWidth("");
      setOriginalSize(true);
    }

    setVerdict(attrSrc ? validateImageUrl(attrSrc) : null);
  };

  const handleAltChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAltTouched(true);
    setAlt(event.target.value);
  };

  const handlePresetClick = (value: number) => {
    setOriginalSize(false);
    setWidth(String(value));
  };

  const handleOriginalSizeChange = (checked: boolean) => {
    setOriginalSize(checked);
    if (checked) {
      setWidth("");
    }
  };

  const handleRemove = () => {
    editor.chain().focus().deleteSelection().run();
    handleOpenChange(false);
  };

  const handleSubmit = () => {
    const finalVerdict = validateImageUrl(url);
    setVerdict(finalVerdict);
    if (!finalVerdict.ok) {
      return;
    }

    let clampedWidth: number | null = null;
    if (!originalSize && width !== "") {
      const parsed = Number(width);
      if (!Number.isNaN(parsed) && parsed > 0) {
        clampedWidth = clampWidth(parsed);
      }
    }

    const attrs = {
      src: url,
      alt: alt || null,
      width: clampedWidth ? String(clampedWidth) : null,
    };

    if (isEditing) {
      editor.chain().focus().updateAttributes("image", attrs).run();
    } else {
      editor.chain().focus().insertContent({ type: "image", attrs }).run();
    }

    handleOpenChange(false);
  };

  const handleUrlKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const widthNumber = width === "" ? null : Number(width);
  const canSubmit = verdict?.ok === true;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={editor.isActive("image") ? "bg-muted" : ""}
          title={editor.isActive("image") ? "Edit image" : "Insert image"}
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="image-url-input">Image URL</Label>
            <Input
              id="image-url-input"
              autoFocus
              value={url}
              placeholder="https://example.com/image.png"
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={handleUrlKeyDown}
            />
            {verdict && !verdict.ok && (
              <p className="text-xs text-destructive">{verdict.error}</p>
            )}
            {verdict && verdict.ok && verdict.warning && (
              <p className="text-xs text-amber-600">{verdict.warning}</p>
            )}
          </div>

          {verdict?.ok && url && (
            <div className="rounded-md border p-2 bg-muted/30">
              <img
                src={url}
                alt=""
                style={{ maxWidth: "100%" }}
                className="max-h-40 mx-auto block"
                onError={() => setPreviewError(true)}
                onLoad={() => setPreviewError(false)}
              />
              {previewError && (
                <p className="text-xs text-muted-foreground mt-1">
                  This URL did not load in the browser. It may still work, but check it.
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="image-alt-input">Alt text</Label>
            <Input
              id="image-alt-input"
              value={alt}
              placeholder="Description of the image"
              onChange={handleAltChange}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="image-width-input">Width (px)</Label>
            <div className="flex gap-1">
              {WIDTH_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant={
                    !originalSize && widthNumber === preset.value
                      ? "secondary"
                      : "outline"
                  }
                  size="sm"
                  disabled={originalSize}
                  onClick={() => handlePresetClick(preset.value)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Input
              id="image-width-input"
              type="number"
              min={MIN_WIDTH}
              max={MAX_WIDTH}
              value={width}
              disabled={originalSize}
              onChange={(event) => setWidth(event.target.value)}
            />
            {!originalSize && widthNumber !== null && widthNumber > CONVENTIONAL_WIDTH && (
              <p className="text-xs text-muted-foreground">
                {CONVENTIONAL_WIDTH}px is the conventional email body width; wider
                images may get a horizontal scrollbar in Outlook desktop.
              </p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="image-original-size"
                checked={originalSize}
                onCheckedChange={(checked) =>
                  handleOriginalSizeChange(checked === true)
                }
              />
              <Label htmlFor="image-original-size" className="font-normal">
                Original size
              </Label>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            {isEditing ? (
              <Button variant="ghost" size="sm" onClick={handleRemove}>
                Remove image
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" disabled={!canSubmit} onClick={handleSubmit}>
              {isEditing ? "Update" : "Insert"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ImageUrlButton;
